import React, { useState } from 'react';
import {
  Button, Dropdown, Modal, message, Progress, Space, Tag, Checkbox,
  Select, Divider, Alert
} from 'antd';
import {
  CheckSquareOutlined, DeleteOutlined, DownloadOutlined,
  CheckCircleOutlined, CloseCircleOutlined, CopyOutlined,
  DownOutlined, ExclamationCircleOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import templateService from 'services/templateService';

/**
 * BulkOperations - Bulk action toolbar for templates
 * Features:
 * - Multi-select templates
 * - Bulk activate/deactivate
 * - Bulk export
 * - Bulk delete with confirmation
 * - Bulk clone
 * - Progress tracking for long operations
 */
const BulkOperations = ({ selectedTemplates, onClearSelection, onRefresh }) => {
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentOperation, setCurrentOperation] = useState('');

  const selectedCount = selectedTemplates?.length || 0;

  const handleBulkActivate = async () => {
    Modal.confirm({
      title: 'Activate Templates',
      content: `Are you sure you want to activate ${selectedCount} template(s)?`,
      icon: <CheckCircleOutlined />,
      okText: 'Activate',
      onOk: async () => {
        await processBulkOperation(
          'activate',
          selectedTemplates,
          async (template) => {
            await templateService.updateTemplate(template.templateId, {
              isActive: true,
              isDraft: false,
            });
          }
        );
      },
    });
  };

  const handleBulkDeactivate = async () => {
    Modal.confirm({
      title: 'Deactivate Templates',
      content: `Are you sure you want to deactivate ${selectedCount} template(s)?`,
      icon: <CloseCircleOutlined />,
      okText: 'Deactivate',
      okType: 'danger',
      onOk: async () => {
        await processBulkOperation(
          'deactivate',
          selectedTemplates,
          async (template) => {
            await templateService.updateTemplate(template.templateId, {
              isActive: false,
            });
          }
        );
      },
    });
  };

  const handleBulkDelete = async () => {
    Modal.confirm({
      title: 'Delete Templates',
      content: (
        <div>
          <Alert
            message="Warning"
            description={`You are about to delete ${selectedCount} template(s). This action cannot be undone.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <p>Are you absolutely sure?</p>
        </div>
      ),
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        await processBulkOperation(
          'delete',
          selectedTemplates,
          async (template) => {
            await templateService.deleteTemplate(template.templateId);
          }
        );
      },
    });
  };

  const handleBulkExport = async () => {
    try {
      setProcessing(true);
      setCurrentOperation('export');
      setProgress(0);

      // Gather all template data
      const exportData = [];
      for (let i = 0; i < selectedTemplates.length; i++) {
        const template = selectedTemplates[i];
        exportData.push(template);
        setProgress(Math.round(((i + 1) / selectedTemplates.length) * 100));
      }

      // Create JSON blob
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });

      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `templates_export_${new Date().getTime()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      message.success(`Exported ${selectedCount} template(s) successfully`);
      onClearSelection?.();
    } catch (error) {
      message.error('Export failed: ' + error.message);
    } finally {
      setProcessing(false);
      setProgress(0);
      setCurrentOperation('');
    }
  };

  const handleBulkClone = async () => {
    Modal.confirm({
      title: 'Clone Templates',
      content: `This will create ${selectedCount} new template(s) as copies. Continue?`,
      icon: <CopyOutlined />,
      okText: 'Clone',
      onOk: async () => {
        await processBulkOperation(
          'clone',
          selectedTemplates,
          async (template) => {
            const clonedTemplate = {
              ...template,
              templateName: `${template.templateName} (Copy)`,
              isDefault: false,
              isDraft: true,
            };
            delete clonedTemplate.templateId;
            delete clonedTemplate.createdAt;
            delete clonedTemplate.updatedAt;

            await templateService.createTemplate(clonedTemplate);
          }
        );
      },
    });
  };

  const processBulkOperation = async (operationType, templates, operationFn) => {
    try {
      setProcessing(true);
      setCurrentOperation(operationType);
      setProgress(0);

      let successCount = 0;
      let failureCount = 0;
      const errors = [];

      for (let i = 0; i < templates.length; i++) {
        try {
          await operationFn(templates[i]);
          successCount++;
        } catch (error) {
          failureCount++;
          errors.push({
            template: templates[i].templateName,
            error: error.message,
          });
        }

        setProgress(Math.round(((i + 1) / templates.length) * 100));
      }

      // Show results
      if (failureCount === 0) {
        message.success(
          `${getOperationVerb(operationType)} ${successCount} template(s) successfully`
        );
      } else {
        Modal.warning({
          title: 'Operation Completed with Errors',
          content: (
            <div>
              <p>
                Success: {successCount} | Failed: {failureCount}
              </p>
              {errors.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <strong>Errors:</strong>
                  <ul style={{ marginTop: 8 }}>
                    {errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        {err.template}: {err.error}
                      </li>
                    ))}
                    {errors.length > 5 && (
                      <li>...and {errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          ),
        });
      }

      onRefresh?.();
      onClearSelection?.();
    } catch (error) {
      message.error(`Bulk ${operationType} failed: ` + error.message);
    } finally {
      setProcessing(false);
      setProgress(0);
      setCurrentOperation('');
    }
  };

  const getOperationVerb = (operation) => {
    const verbs = {
      activate: 'Activated',
      deactivate: 'Deactivated',
      delete: 'Deleted',
      export: 'Exported',
      clone: 'Cloned',
    };
    return verbs[operation] || operation;
  };

  const menuItems = [
    {
      key: 'activate',
      label: 'Activate All',
      icon: <CheckCircleOutlined />,
      onClick: handleBulkActivate,
    },
    {
      key: 'deactivate',
      label: 'Deactivate All',
      icon: <CloseCircleOutlined />,
      onClick: handleBulkDeactivate,
    },
    {
      key: 'clone',
      label: 'Clone All',
      icon: <CopyOutlined />,
      onClick: handleBulkClone,
    },
    {
      type: 'divider',
    },
    {
      key: 'export',
      label: 'Export Selected',
      icon: <DownloadOutlined />,
      onClick: handleBulkExport,
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: 'Delete All',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: handleBulkDelete,
    },
  ];

  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      <Container>
        <LeftSection>
          <CheckSquareOutlined />
          <Tag color="blue">{selectedCount} Selected</Tag>
          <Button
            size="small"
            type="link"
            onClick={onClearSelection}
          >
            Clear Selection
          </Button>
        </LeftSection>

        <RightSection>
          <Space>
            <Button
              icon={<CheckCircleOutlined />}
              onClick={handleBulkActivate}
              disabled={processing}
            >
              Activate
            </Button>

            <Button
              icon={<CloseCircleOutlined />}
              onClick={handleBulkDeactivate}
              disabled={processing}
            >
              Deactivate
            </Button>

            <Button
              icon={<DownloadOutlined />}
              onClick={handleBulkExport}
              disabled={processing}
            >
              Export
            </Button>

            <Dropdown menu={{ items: menuItems }} trigger={['click']}>
              <Button disabled={processing}>
                More Actions <DownOutlined />
              </Button>
            </Dropdown>
          </Space>
        </RightSection>
      </Container>

      {/* Progress Modal */}
      <Modal
        title={`Processing: ${getOperationVerb(currentOperation)}...`}
        open={processing}
        footer={null}
        closable={false}
        centered
      >
        <ProgressContainer>
          <Progress percent={progress} status="active" />
          <ProgressText>
            {currentOperation === 'export'
              ? `Preparing ${selectedCount} template(s) for export...`
              : `Processing ${selectedCount} template(s)...`}
          </ProgressText>
        </ProgressContainer>
      </Modal>
    </>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 4px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  .anticon {
    font-size: 18px;
    color: #1890ff;
  }
`;

const RightSection = styled.div``;

const ProgressContainer = styled.div`
  padding: 20px 0;
`;

const ProgressText = styled.div`
  text-align: center;
  margin-top: 16px;
  color: #595959;
`;

export default BulkOperations;
